/**
 * Step 2: Cadence Steps builder with flow editor and right-side drawer.
 * Handles step selection, drawer editing, email content, wait/condition configuration.
 */
import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@evertrue/et-components';
import CadenceFlowEditor from './cadence-flow-editor';
import CadenceEmailContent from './step-components/cadence-email-content';
import AddTaskTypeModal from './modals/add-task-type-modal';
import ImportJourneyModal from './modals/import-journey-modal';
import CharacterCounter from 'apps/interactions/components/character-counter';
import {
  getTaskTypeInfo,
  TASK_STEP_NAME_MAX_LENGTH,
  TASK_STEP_DESCRIPTION_MAX_LENGTH,
} from '../../utils/cadence-step-utils';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';

const STEP_TYPE_ICONS = {
  email: 'email',
  condition: 'filter-list',
  exit: 'exit-to-app',
  wait: 'access-time',
};

const MIN_DRAWER_WIDTH = 380;
const MAX_DRAWER_WIDTH = 800;

const CadenceBuilderStep = ({
  steps,
  setSteps,
  selectedStep,
  selectedStepIndex,
  selectedBranch,
  drawerTab,
  setDrawerTab,
  typeLabel,
  isProspectPlan,
  isDebugMode,
  stepTypeLabels,
  onStepSelect,
  onBranchSelect,
  onDrawerClose,
  onDrawerFieldChange,
  outreachTemplates,
  signatures,
  onTemplateSelect,
  onSignatureChange,
  onTemplateUnlink,
  domainStatus,
  isOwner,
  flowLocked,
}) => {
  const [drawerWidth, setDrawerWidth] = useState(MIN_DRAWER_WIDTH);
  const [showTaskTypePicker, setShowTaskTypePicker] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isDragging = useRef(false);
  const { flagEnabled: cadenceConverterEnabled } = useFeatureFlag('cadenceConverter');

  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    const startX = e.clientX;
    const startWidth = drawerWidth;

    const onMouseMove = (ev) => {
      if (!isDragging.current) return;
      const delta = startX - ev.clientX;
      setDrawerWidth(Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, startWidth + delta)));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const getStepIcon = () => {
    if (selectedStep?.type === 'task') {
      return getTaskTypeInfo(selectedStep.taskType)?.icon || 'assignment';
    }
    return STEP_TYPE_ICONS[selectedStep?.type] || 'access-time';
  };

  const getStepTypeLabel = () => {
    if (selectedStep?.type === 'task') {
      return getTaskTypeInfo(selectedStep.taskType)?.label || 'Manual Task';
    }
    return stepTypeLabels[selectedStep?.type] || selectedStep?.type;
  };

  return (
    <div className="create-cadence-builder">
      <div className={`create-cadence-builder--main ${selectedStep ? 'has-drawer' : ''}`}>
        <div className="create-cadence-builder--flow-header">
          <h2 className="create-cadence-builder--flow-title">{typeLabel} Flow</h2>
          {!flowLocked && steps.length === 0 && cadenceConverterEnabled && (
            <button
              type="button"
              className="create-cadence-builder--import-btn"
              onClick={() => setShowImportModal(true)}
              title="Import steps from Cadence 1.0"
            >
              <Icon icon="upload" />
              Import from Cadence 1.0
            </button>
          )}
        </div>
        <div className="create-cadence-builder--flow-editor">
          <CadenceFlowEditor
            steps={steps}
            onStepsChange={setSteps}
            selectedStepIndex={selectedStepIndex}
            onStepSelect={onStepSelect}
            selectedBranch={selectedBranch}
            onBranchSelect={onBranchSelect}
            onCloseDrawer={onDrawerClose}
            domainStatus={domainStatus}
            isOwner={isOwner}
            flowLocked={flowLocked}
          />
        </div>
      </div>

      {selectedStep && (
        <div className="create-cadence-drawer" style={{ width: drawerWidth }}>
          <div className="create-cadence-drawer--resize-handle" onMouseDown={handleResizeMouseDown} />

          <div className="create-cadence-drawer--header">
            <h3 className="create-cadence-drawer--title">Step Details</h3>
            <button type="button" className="create-cadence-drawer--close" onClick={onDrawerClose}>
              <Icon icon="close" />
            </button>
          </div>

          {/* Email steps always show both tabs */}
          {selectedStep.type === 'email' && (
            <div className="create-cadence-drawer--tabs">
              <button
                type="button"
                className={`create-cadence-drawer--tab ${drawerTab === 'details' ? 'is-active' : ''}`}
                onClick={() => setDrawerTab('details')}
              >
                Details
              </button>
              <button
                type="button"
                className={`create-cadence-drawer--tab ${drawerTab === 'email_content' ? 'is-active' : ''}`}
                onClick={() => setDrawerTab('email_content')}
              >
                Email Content
                {(!selectedStep.subjectOverride || !selectedStep.emailBody) && (
                  <span className="create-cadence-drawer--tab-required">*</span>
                )}
              </button>
            </div>
          )}

          <div className="create-cadence-drawer--body">
            {(drawerTab === 'details' || selectedStep.type !== 'email') && (
              <>
                <div className="create-cadence-drawer--field">
                  <label className="create-cadence-drawer--label">Step Name</label>
                  <input
                    type="text"
                    className="create-cadence-drawer--input"
                    value={selectedStep.name != null ? selectedStep.name : stepTypeLabels[selectedStep.type] || ''}
                    placeholder={stepTypeLabels[selectedStep.type] || 'Step name'}
                    onChange={(e) => onDrawerFieldChange('name', e.target.value)}
                    maxLength={selectedStep.type === 'task' ? TASK_STEP_NAME_MAX_LENGTH : undefined}
                  />
                  {selectedStep.type === 'task' && (
                    <CharacterCounter limit={TASK_STEP_NAME_MAX_LENGTH} count={(selectedStep.name || '').length} />
                  )}
                </div>
                <div className="create-cadence-drawer--field">
                  <label className="create-cadence-drawer--label">Description</label>
                  <textarea
                    className="create-cadence-drawer--textarea"
                    rows="3"
                    placeholder="Brief description of what this step does"
                    value={selectedStep.description || ''}
                    onChange={(e) => onDrawerFieldChange('description', e.target.value)}
                    maxLength={selectedStep.type === 'task' ? TASK_STEP_DESCRIPTION_MAX_LENGTH : undefined}
                  />
                  {selectedStep.type === 'task' && (
                    <CharacterCounter
                      limit={TASK_STEP_DESCRIPTION_MAX_LENGTH}
                      count={(selectedStep.description || '').length}
                    />
                  )}
                </div>
                {selectedStep.type !== 'condition' && (
                  <div className="create-cadence-drawer--field">
                    <label className="create-cadence-drawer--label">Step Type</label>
                    <div className="create-cadence-drawer--type-display">
                      <Icon icon={getStepIcon()} />
                      <span>{getStepTypeLabel()}</span>
                      {selectedStep.type === 'task' && (
                        <button
                          type="button"
                          className="create-cadence-drawer--change-type-btn"
                          onClick={() => setShowTaskTypePicker(true)}
                        >
                          Change
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {selectedStep.type === 'task' && isDebugMode && (
                  <div className="create-cadence-drawer--field create-cadence-drawer--debug-field">
                    <label className="create-cadence-drawer--debug-label">
                      <input
                        type="checkbox"
                        checked={selectedStep.skipBusinessDayCheck || false}
                        onChange={(e) => onDrawerFieldChange('skipBusinessDayCheck', e.target.checked)}
                      />
                      <span>Skip business day check (debug)</span>
                    </label>
                  </div>
                )}
              </>
            )}

            {selectedStep.type === 'email' && (
              <>
                {drawerTab === 'details' && isDebugMode && (
                  <div className="create-cadence-drawer--field create-cadence-drawer--debug-field">
                    <label className="create-cadence-drawer--debug-label">
                      <input
                        type="checkbox"
                        checked={selectedStep.skipBusinessDayCheck || false}
                        onChange={(e) => onDrawerFieldChange('skipBusinessDayCheck', e.target.checked)}
                      />
                      <span>Skip business day check (debug)</span>
                    </label>
                    <div className="create-cadence-drawer--debug-hint">
                      Allows sending on weekends/holidays. Debug mode only.
                    </div>
                  </div>
                )}
                {drawerTab === 'email_content' && (
                  <CadenceEmailContent
                    key={selectedStep.id ? `email-content-${selectedStep.id}` : undefined}
                    subject={selectedStep.subjectOverride}
                    onSubjectChange={(val) => onDrawerFieldChange('subjectOverride', val)}
                    body={selectedStep.emailBody}
                    onBodyChange={(val) => onDrawerFieldChange('emailBody', val)}
                    template={selectedStep.template}
                    templateOriginalSubject={selectedStep.templateOriginalSubject}
                    templateOriginalBody={selectedStep.templateOriginalBody}
                    onTemplateSelect={onTemplateSelect}
                    onTemplateUnlink={onTemplateUnlink}
                    signature={selectedStep.signature}
                    onSignatureChange={onSignatureChange}
                    outreachTemplates={outreachTemplates}
                    signatures={signatures}
                    stepId={selectedStep.id}
                    initialAttachments={selectedStep.attachments}
                    onAttachmentsChange={(attachments) => onDrawerFieldChange('attachments', attachments)}
                    isAutomated={selectedStep.isAutomated !== false}
                    editorKey={selectedStep.id ? `editor-${selectedStep.id}` : undefined}
                  />
                )}
              </>
            )}

            {selectedStep.type === 'wait' && (
              <div className="create-cadence-drawer--field">
                <label className="create-cadence-drawer--label">Duration</label>
                <div className="create-cadence-drawer--wait-row">
                  <input
                    type="number"
                    className="create-cadence-drawer--input create-cadence-drawer--input-small"
                    min="1"
                    value={
                      selectedStep.waitDuration === '' || selectedStep.waitDuration == null
                        ? ''
                        : selectedStep.waitDuration
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        onDrawerFieldChange('waitDuration', '');
                      } else {
                        const parsed = parseInt(raw, 10);
                        onDrawerFieldChange('waitDuration', isNaN(parsed) ? '' : parsed);
                      }
                    }}
                    onBlur={() => {
                      const val = selectedStep.waitDuration;
                      if (val === '' || val == null || val < 1) {
                        onDrawerFieldChange('waitDuration', 1);
                      }
                    }}
                  />
                  <select
                    className="create-cadence-drawer--select"
                    value={selectedStep.waitUnit || 'days'}
                    onChange={(e) => onDrawerFieldChange('waitUnit', e.target.value)}
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          <div className="create-cadence-drawer--footer">
            <Button type="secondary" onClick={onDrawerClose}>
              Close
            </Button>
          </div>
        </div>
      )}

      <AddTaskTypeModal
        isOpen={showTaskTypePicker}
        onClose={() => setShowTaskTypePicker(false)}
        onSelectTaskType={(taskTypeInfo) => {
          onDrawerFieldChange('taskType', taskTypeInfo.id);
          setShowTaskTypePicker(false);
        }}
      />

      <ImportJourneyModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={(importedSteps) => setSteps(importedSteps)}
      />
    </div>
  );
};

CadenceBuilderStep.propTypes = {
  steps: PropTypes.array.isRequired,
  setSteps: PropTypes.func.isRequired,
  selectedStep: PropTypes.object,
  selectedStepIndex: PropTypes.number,
  selectedBranch: PropTypes.object,
  drawerTab: PropTypes.string,
  setDrawerTab: PropTypes.func,
  typeLabel: PropTypes.string,
  isProspectPlan: PropTypes.bool,
  isDebugMode: PropTypes.bool,
  stepTypeLabels: PropTypes.object,
  onStepSelect: PropTypes.func,
  onBranchSelect: PropTypes.func,
  onDrawerClose: PropTypes.func,
  onDrawerFieldChange: PropTypes.func,
  outreachTemplates: PropTypes.array,
  signatures: PropTypes.array,
  onTemplateSelect: PropTypes.func,
  onSignatureChange: PropTypes.func,
  onTemplateUnlink: PropTypes.func,
  domainStatus: PropTypes.oneOf(['not_configured', 'pending', 'configured']),
  isOwner: PropTypes.bool,
  flowLocked: PropTypes.bool,
};

export default CadenceBuilderStep;
